import { useQuery } from '@tanstack/react-query';
import type { MetricEnvelope, WindowKind } from '@elutide/shared';
import { api } from './api';
import { qk } from './keys';

export interface MetricsResult {
  /** Envelope by metric id. A money metric a role may not see is absent, not zero. */
  get: (id: string) => MetricEnvelope | undefined;
  envelopes: readonly MetricEnvelope[];
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
  /** The oldest as_of among the envelopes, which is what a card's age chip should show. */
  asOf: string | null;
}

function dimsParam(dims?: Readonly<Record<string, string>>): string | undefined {
  if (!dims) return undefined;
  const entries = Object.entries(dims);
  if (entries.length === 0) return undefined;
  return entries.map(([key, value]) => `${key}:${value}`).join(',');
}

/**
 * One batched read of `GET /metrics`. Screens never compute a number; they name metric ids
 * and render what comes back inside its envelope.
 *
 * A metric the caller's role may not see is omitted by the server rather than zeroed, so
 * `get` returning undefined is the normal case for a packer, not an error.
 */
export function useMetrics(
  screenId: string,
  ids: readonly string[],
  window: WindowKind = '30d',
  dims?: Readonly<Record<string, string>>,
  options: { enabled?: boolean; staleTime?: number } = {},
): MetricsResult {
  const query = useQuery({
    queryKey: [...qk.metrics(screenId, window), dimsParam(dims) ?? 'nodims'],
    queryFn: () =>
      api.get<{ metrics: MetricEnvelope[] }>('/metrics', {
        query: {
          ids: [...ids],
          window,
          ...(dimsParam(dims) ? { dims: dimsParam(dims) } : {}),
        },
      }),
    enabled: options.enabled ?? ids.length > 0,
    ...(options.staleTime === undefined ? {} : { staleTime: options.staleTime }),
  });

  const envelopes = query.data?.metrics ?? [];
  const byId = new Map(envelopes.map((envelope) => [envelope.metric_id, envelope]));
  const asOf = envelopes.reduce<string | null>((oldest, envelope) => {
    if (!oldest) return envelope.as_of;
    return Date.parse(envelope.as_of) < Date.parse(oldest) ? envelope.as_of : oldest;
  }, null);

  return {
    get: (id) => byId.get(id),
    envelopes,
    isLoading: query.isLoading,
    error: query.error,
    refetch: () => void query.refetch(),
    asOf,
  };
}

/** A single metric with its own key, so invalidating it does not disturb the screen batch. */
export function useMetric(
  id: string,
  window: WindowKind = '30d',
  dims?: Readonly<Record<string, string>>,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: qk.metric(id, window, dims ?? null),
    queryFn: async () => {
      const response = await api.get<{ metrics: MetricEnvelope[] }>('/metrics', {
        query: {
          ids: [id],
          window,
          ...(dimsParam(dims) ? { dims: dimsParam(dims) } : {}),
        },
      });
      return response.metrics[0] ?? null;
    },
    enabled: options.enabled ?? true,
  });
}
